class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("[data-cart-discount-form]");
    this.messageElement = this.querySelector("[data-cart-discount-message]");

    if (!this.form) return;

    this._listenerController?.abort();
    this._listenerController = new AbortController();
    const options = { signal: this._listenerController.signal };
    this.form.addEventListener("submit", this._onSubmit.bind(this), options);
    document.addEventListener("click", this._onRemoveClick.bind(this), options);
  }

  disconnectedCallback() {
    this._listenerController?.abort();
  }

  _getAppliedCodes() {
    const scope = this.closest("cart-drawer, .shopify-section--main-cart, .shopify-section--velio-cart") || document;
    return [...new Set(Array.from(scope.querySelectorAll("[data-cart-discount-code]"))
      .map((element) => element.dataset.cartDiscountCode)
      .filter(Boolean))];
  }

  async _onSubmit(event) {
    event.preventDefault();

    const input = this.form.querySelector('input[name="discount"]');
    const code = input.value.trim();

    if (code === "") return;

    const codes = this._getAppliedCodes();

    if (!codes.some((appliedCode) => appliedCode.toLowerCase() === code.toLowerCase())) {
      codes.push(code);
    }

    await this._updateDiscount(codes.join(","));
  }

  async _onRemoveClick(event) {
    const button = event.target.closest("[data-cart-discount-remove]");

    if (!button) return;

    const scope = button.closest(".shopify-section--main-cart, .shopify-section--velio-cart") || button.closest("cart-drawer");

    if (scope && !scope.contains(this)) return;

    event.preventDefault();

    const code = button.dataset.cartDiscountRemove;
    const codes = this._getAppliedCodes().filter((appliedCode) => appliedCode !== code);

    await this._updateDiscount(codes.join(","));
  }

  async _updateDiscount(discount) {
    const submitButton = this.form?.querySelector('button[type="submit"]');

    if (submitButton) submitButton.disabled = true;
    this._showMessage("", false);

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({ discount })
      });

      const cart = await response.json();

      if (!response.ok) {
        throw new Error(cart.description || cart.message || "");
      }

      if (discount !== "") {
        const requestedCodes = discount.split(",").map((code) => code.trim().toLowerCase());
        const appliedCodes = [
          ...(cart.discount_codes || []).filter(entry => entry.applicable).map(entry => entry.code),
          ...(cart.cart_level_discount_applications || []).filter(entry => entry.type === "discount_code").map(entry => entry.title),
          ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).filter(entry => entry.discount_application?.type === "discount_code").map(entry => entry.discount_application.title))
        ].map(code => code.trim().toLowerCase());

        if (!requestedCodes.every((code) => appliedCodes.includes(code))) {
          throw new Error("");
        }
      }

      this._showMessage(window.themeVariables.strings.discountCodeApplied, false);

      if (this.closest(".shopify-section--main-cart, .shopify-section--velio-cart")) {
        window.location.reload();
      } else {
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      }
    } catch (error) {
      const message = error.message && error.message !== "" ? error.message : window.themeVariables.strings.discountCodeError;

      this._showMessage(message, true);
      this.form?.querySelector('input[name="discount"]')?.focus();
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  }

  _showMessage(message, isError) {
    if (!this.messageElement) return;

    this.messageElement.textContent = message;
    this.messageElement.hidden = message === "";
    this.messageElement.classList.toggle("text-error", isError);
  }
}

if (!window.customElements.get("cart-discount")) {
  window.customElements.define("cart-discount", CartDiscount);
}


// Native details remains usable before this enhancement loads or when JavaScript is disabled.
class VelioPromo extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.details?.querySelector('summary');
    this.content = this.querySelector('.vc-cart-promo__content');
    if (!this.summary || !this.content) return;
    this.expanded = this.details.open;
    this.controller = new AbortController();
    this.summary.addEventListener('click', event => {
      event.preventDefault();
      this.setExpanded(!this.expanded);
    }, { signal: this.controller.signal });
  }
  setExpanded(expanded, animate = true) {
    const wasOpen = this.details.open;
    const fromHeight = this.details.getBoundingClientRect().height;
    const fromOpacity = wasOpen ? getComputedStyle(this.content).opacity : '0';
    this.animation?.cancel();
    this.contentAnimation?.cancel();
    this.expanded = expanded;
    this.summary.setAttribute('aria-expanded', String(expanded));
    this.content.inert = !expanded;
    if (!expanded && this.content.contains(document.activeElement)) this.summary.focus({ preventScroll: true });
    if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.details.open = expanded;
      this.details.style.height = '';
      this.details.style.overflow = '';
      return;
    }
    this.details.open = true;
    const toHeight = this.summary.getBoundingClientRect().height + (expanded ? this.content.getBoundingClientRect().height : 0);
    this.details.style.overflow = 'hidden';
    this.details.style.height = fromHeight + 'px';
    const options = { duration: 240, easing: 'cubic-bezier(.22,1,.36,1)' };
    const animation = this.animation = this.details.animate({ height: [fromHeight + 'px', toHeight + 'px'] }, options);
    this.contentAnimation = this.content.animate({ opacity: [fromOpacity, expanded ? 1 : 0], transform: [expanded && !wasOpen ? 'translateY(-6px)' : 'translateY(0)', 'translateY(0)'] }, options);
    animation.finished.then(() => {
      if (this.animation !== animation) return;
      this.details.open = this.expanded;
      this.details.style.height = '';
      this.details.style.overflow = '';
    }).catch(() => {});
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.animation?.cancel();
    this.contentAnimation?.cancel();
  }
}
if (!customElements.get('velio-promo')) customElements.define('velio-promo', VelioPromo);

// Session countdown extends the existing drawer; cart events keep one shared start.
const cartTimerKey = 'velio-cart-start';
let cartTimerStart;
try { cartTimerStart = Number(sessionStorage.getItem(cartTimerKey)); } catch {}
const saveCartStart = () => {
  cartTimerStart = Date.now();
  try { sessionStorage.setItem(cartTimerKey, String(cartTimerStart)); } catch {}
};
document.addEventListener('variant:add', () => {
  saveCartStart();
  document.querySelectorAll('cart-reservation-timer').forEach(timer => timer.tick());
});
class CartReservationTimer extends HTMLElement {
  connectedCallback() {
    if (!Number.isFinite(cartTimerStart) || cartTimerStart <= 0) saveCartStart();
    this.drawer = this.closest('cart-drawer');
    this.controller = new AbortController();
    this.syncVisibility = () => {
      clearInterval(this.interval);
      this.tick();
      if (!document.hidden && (!this.drawer || this.drawer.hasAttribute('open'))) {
        this.interval = setInterval(() => this.tick(), 1000);
      }
    };
    for (const eventName of ['dialog:after-show', 'dialog:after-hide']) {
      this.drawer?.addEventListener(eventName, this.syncVisibility, {signal: this.controller.signal});
    }
    document.addEventListener('visibilitychange', this.syncVisibility, {signal: this.controller.signal});
    this.syncVisibility();
  }
  tick() {
    const seconds = Math.max(0, Math.min(600, Math.ceil((cartTimerStart + 600000 - Date.now()) / 1000)));
    const time = this.querySelector('[data-cart-time]');
    if (time) time.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }
  disconnectedCallback() { clearInterval(this.interval); this.controller?.abort(); }
}
if (!customElements.get('cart-reservation-timer')) customElements.define('cart-reservation-timer', CartReservationTimer);
